import React from 'react';
import { Button } from '../../components/ui/button';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '../../components/ui/accordion';
import { Save } from 'lucide-react';
import { defaultHomeContent } from '../../siteContent';
import { useCms, getIn, Text, Lines, ImageField } from './cmsShared';

const AdminContent = () => {
  const { content, update, uploadImage, save, saving } = useCms('home', defaultHomeContent);
  const c = content;

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="font-heading text-xl font-bold text-[#003366]">Homepage Content</h2>
          <p className="text-sm text-gray-500">Edit every heading, text and image on the home page and the logo.</p>
        </div>
        <Button onClick={save} disabled={saving} data-testid="cms-save-btn" className="rounded-full bg-[#E74C3C] hover:bg-[#C0392B]"><Save className="w-4 h-4 mr-2" />{saving ? 'Saving…' : 'Save Changes'}</Button>
      </div>

      <Accordion type="multiple" defaultValue={['logo', 'hero']} className="space-y-2">
        <AccordionItem value="logo" className="border rounded-xl px-4 bg-white">
          <AccordionTrigger data-testid="cms-section-logo">Logo</AccordionTrigger>
          <AccordionContent><ImageField label="Site Logo (header & footer)" value={c.logo} onChange={(v) => update(['logo'], v)} onUpload={(f) => uploadImage(['logo'], f)} testid="cms-upload-logo" /></AccordionContent>
        </AccordionItem>

        <AccordionItem value="hero" className="border rounded-xl px-4 bg-white">
          <AccordionTrigger data-testid="cms-section-hero">Hero Section</AccordionTrigger>
          <AccordionContent>
            <Text label="Badge" value={c.hero.badge} onChange={(v) => update(['hero', 'badge'], v)} testid="cms-hero-badge" />
            <Text label="Headline (main text)" value={c.hero.headlinePre} onChange={(v) => update(['hero', 'headlinePre'], v)} area testid="cms-hero-headlinePre" />
            <Text label="Headline (highlighted word)" value={c.hero.headlineHighlight} onChange={(v) => update(['hero', 'headlineHighlight'], v)} />
            <Text label="Subtext" value={c.hero.subtext} onChange={(v) => update(['hero', 'subtext'], v)} area />
            <div className="grid grid-cols-2 gap-3">
              <Text label="Primary Button" value={c.hero.ctaPrimary} onChange={(v) => update(['hero', 'ctaPrimary'], v)} />
              <Text label="Secondary Button" value={c.hero.ctaSecondary} onChange={(v) => update(['hero', 'ctaSecondary'], v)} />
            </div>
            <Text label="Explore link label" value={c.hero.exploreLabel} onChange={(v) => update(['hero', 'exploreLabel'], v)} />
            <div className="grid grid-cols-2 gap-3">
              <Text label="Stat number" value={c.hero.statNumber} onChange={(v) => update(['hero', 'statNumber'], v)} />
              <Text label="Stat label" value={c.hero.statLabel} onChange={(v) => update(['hero', 'statLabel'], v)} />
            </div>
            <ImageField label="Hero Image" value={c.hero.image} onChange={(v) => update(['hero', 'image'], v)} onUpload={(f) => uploadImage(['hero', 'image'], f)} testid="cms-upload-hero-image" />
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="trust" className="border rounded-xl px-4 bg-white">
          <AccordionTrigger>Trust Bar</AccordionTrigger>
          <AccordionContent><Lines label="Trust bar items" value={c.trustBar} onChange={(v) => update(['trustBar'], v)} /></AccordionContent>
        </AccordionItem>

        <AccordionItem value="who" className="border rounded-xl px-4 bg-white">
          <AccordionTrigger>Who We Are</AccordionTrigger>
          <AccordionContent>
            <Text label="Eyebrow" value={c.whoWeAre.eyebrow} onChange={(v) => update(['whoWeAre', 'eyebrow'], v)} />
            <Text label="Heading line 1" value={c.whoWeAre.headingLine1} onChange={(v) => update(['whoWeAre', 'headingLine1'], v)} />
            <Text label="Heading highlight" value={c.whoWeAre.headingHighlight} onChange={(v) => update(['whoWeAre', 'headingHighlight'], v)} />
            <Text label="Paragraph 1" value={c.whoWeAre.para1} onChange={(v) => update(['whoWeAre', 'para1'], v)} area />
            <Text label="Paragraph 2" value={c.whoWeAre.para2} onChange={(v) => update(['whoWeAre', 'para2'], v)} area />
            <Lines label="Ecosystem steps" value={c.ecosystem} onChange={(v) => update(['ecosystem'], v)} />
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="support" className="border rounded-xl px-4 bg-white">
          <AccordionTrigger>How We Support (Pillars)</AccordionTrigger>
          <AccordionContent>
            <Text label="Eyebrow" value={c.support.eyebrow} onChange={(v) => update(['support', 'eyebrow'], v)} />
            <Text label="Heading" value={c.support.heading} onChange={(v) => update(['support', 'heading'], v)} />
            {(c.support.pillars || []).map((p, i) => (
              <div key={i} className="border rounded-lg p-3 mb-3 bg-gray-50">
                <Text label={`Pillar ${i + 1} title`} value={p.title} onChange={(v) => update(['support', 'pillars', i, 'title'], v)} />
                <Lines label={`Pillar ${i + 1} points`} value={p.points} onChange={(v) => update(['support', 'pillars', i, 'points'], v)} />
              </div>
            ))}
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="difference" className="border rounded-xl px-4 bg-white">
          <AccordionTrigger>Our Difference</AccordionTrigger>
          <AccordionContent>
            <Text label="Eyebrow" value={c.difference.eyebrow} onChange={(v) => update(['difference', 'eyebrow'], v)} />
            <Text label="Heading" value={c.difference.heading} onChange={(v) => update(['difference', 'heading'], v)} />
            <Lines label="Difference items" value={c.difference.items} onChange={(v) => update(['difference', 'items'], v)} />
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="will" className="border rounded-xl px-4 bg-white">
          <AccordionTrigger>Will / Family Protection</AccordionTrigger>
          <AccordionContent>
            <Text label="Eyebrow" value={c.will.eyebrow} onChange={(v) => update(['will', 'eyebrow'], v)} />
            <Text label="Heading" value={c.will.heading} onChange={(v) => update(['will', 'heading'], v)} />
            <Text label="Paragraph" value={c.will.para} onChange={(v) => update(['will', 'para'], v)} area />
            <Text label="Note" value={c.will.note} onChange={(v) => update(['will', 'note'], v)} area />
            <Text label="Button" value={c.will.cta} onChange={(v) => update(['will', 'cta'], v)} />
            <Lines label="Points" value={c.will.points} onChange={(v) => update(['will', 'points'], v)} />
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="community" className="border rounded-xl px-4 bg-white">
          <AccordionTrigger>Community Model</AccordionTrigger>
          <AccordionContent>
            <Text label="Eyebrow" value={c.community.eyebrow} onChange={(v) => update(['community', 'eyebrow'], v)} />
            <Text label="Heading" value={c.community.heading} onChange={(v) => update(['community', 'heading'], v)} />
            <Text label="Paragraph" value={c.community.para} onChange={(v) => update(['community', 'para'], v)} area />
            <Lines label="Credit flow steps" value={c.community.flow} onChange={(v) => update(['community', 'flow'], v)} />
            <Lines label="Redeem options" value={c.community.redeem} onChange={(v) => update(['community', 'redeem'], v)} />
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="events" className="border rounded-xl px-4 bg-white">
          <AccordionTrigger>Events Section</AccordionTrigger>
          <AccordionContent>
            <Text label="Eyebrow" value={c.events.eyebrow} onChange={(v) => update(['events', 'eyebrow'], v)} />
            <Text label="Heading" value={c.events.heading} onChange={(v) => update(['events', 'heading'], v)} />
            <p className="text-xs text-gray-400">Event cards are managed in the Events tab.</p>
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="impact" className="border rounded-xl px-4 bg-white">
          <AccordionTrigger>Impact Stats</AccordionTrigger>
          <AccordionContent>
            <Text label="Eyebrow" value={c.impact.eyebrow} onChange={(v) => update(['impact', 'eyebrow'], v)} />
            <Text label="Heading" value={c.impact.heading} onChange={(v) => update(['impact', 'heading'], v)} />
            {(c.impact.stats || []).map((s, i) => (
              <div key={i} className="grid grid-cols-3 gap-2 mb-2">
                <Text label={`#${i + 1} value`} value={s.value} onChange={(v) => update(['impact', 'stats', i, 'value'], v)} />
                <Text label="suffix" value={s.suffix} onChange={(v) => update(['impact', 'stats', i, 'suffix'], v)} />
                <Text label="label" value={s.label} onChange={(v) => update(['impact', 'stats', i, 'label'], v)} />
              </div>
            ))}
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="testimonials" className="border rounded-xl px-4 bg-white">
          <AccordionTrigger>Testimonials</AccordionTrigger>
          <AccordionContent>
            <Text label="Eyebrow" value={c.testimonials.eyebrow} onChange={(v) => update(['testimonials', 'eyebrow'], v)} />
            <Text label="Heading" value={c.testimonials.heading} onChange={(v) => update(['testimonials', 'heading'], v)} />
            {(c.testimonials.items || []).map((t, i) => (
              <div key={i} className="border rounded-lg p-3 mb-3 bg-gray-50">
                <Text label={`Quote ${i + 1}`} value={t.quote} onChange={(v) => update(['testimonials', 'items', i, 'quote'], v)} area />
                <div className="grid grid-cols-2 gap-2">
                  <Text label="Name" value={t.name} onChange={(v) => update(['testimonials', 'items', i, 'name'], v)} />
                  <Text label="Role" value={t.role} onChange={(v) => update(['testimonials', 'items', i, 'role'], v)} />
                </div>
              </div>
            ))}
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="finalcta" className="border rounded-xl px-4 bg-white">
          <AccordionTrigger>Final CTA</AccordionTrigger>
          <AccordionContent>
            <Text label="Heading line 1" value={c.finalCta.headingLine1} onChange={(v) => update(['finalCta', 'headingLine1'], v)} />
            <Text label="Heading line 2" value={c.finalCta.headingLine2} onChange={(v) => update(['finalCta', 'headingLine2'], v)} />
            <Text label="Subtext" value={c.finalCta.subtext} onChange={(v) => update(['finalCta', 'subtext'], v)} area />
            <Text label="Button" value={c.finalCta.cta} onChange={(v) => update(['finalCta', 'cta'], v)} />
          </AccordionContent>
        </AccordionItem>
      </Accordion>

      <div className="flex justify-end mt-6">
        <Button onClick={save} disabled={saving} className="rounded-full bg-[#E74C3C] hover:bg-[#C0392B]"><Save className="w-4 h-4 mr-2" />{saving ? 'Saving…' : 'Save Changes'}</Button>
      </div>
    </div>
  );
};

export default AdminContent;
